import { useMutation } from '@tanstack/react-query';
import { CheckBox } from 'grommet';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import OneSignal from 'react-onesignal';
import { useDispatch } from 'react-redux';

import { postUserMarketingOptInMutationFn } from '@/queries/users';
import { useSelector } from '@/redux/helper';
import { setUserData } from '@/redux/slices/user/actions';
import { UserNotificationTag } from '@/shared/pushNotifications/types';

import { ButtonContainer, Content, FieldError, FieldLabel, Header, HeaderTitle, PrimaryButton, Section } from './Section';

const getCurrentUserTags = () =>
  Object.fromEntries(Object.entries(OneSignal.User.getTags() || {}).map(([key, value]) => [key, value === '1'])) as Record<
    UserNotificationTag,
    boolean
  >;

const MarketingOptInSection = () => {
  const dispatch = useDispatch();
  const {
    data,
    isPending,
    isSuccess,
    error,
    mutate: updateMarketingOptIn,
  } = useMutation({
    mutationFn: postUserMarketingOptInMutationFn,
    onSuccess: (data) => {
      if (data?.user) {
        dispatch(setUserData(data.user));
      }
    },
  });

  const isOneSignalAvailable = typeof window !== 'undefined' && 'OneSignal' in window;
  const isPushSupported = isOneSignalAvailable && OneSignal.Notifications.isPushSupported();
  const pushPermissionNative = isPushSupported ? OneSignal.Notifications.permissionNative : undefined;
  // push opt-ins stay visible but disabled (greyed out) until permission is granted.
  const isPushGranted = isPushSupported && OneSignal.Notifications.permission;

  const errorMessage = data?.user ? '' : data?.error || (error ? 'Something Went Wrong, please try again!' : '');

  const userMarketingOptIn = useSelector((state) => state.user.isMarketingOptIn ?? false);
  const [formData, setFormData] = useState(() => {
    const tags = getCurrentUserTags();
    return {
      isMarketingOptIn: userMarketingOptIn,
      ...tags,
    };
  });

  // get tags from onesignal after 1s again to let it update from the server
  useEffect(() => {
    const id = setTimeout(() => {
      const tags = getCurrentUserTags();
      setFormData((p) => ({ ...p, ...tags }));
    }, 1000);
    return () => clearTimeout(id);
  }, []);

  const updateHandler = () => {
    const data = {
      isMarketingOptIn: formData.isMarketingOptIn,
      notificationTags:
        isOneSignalAvailable && OneSignal.Notifications.permission
          ? {
              postReactions: formData.postReactions ? '1' : '0',
              animalActivityAlerts: formData.animalActivityAlerts ? '1' : '0',
              topHighlights: formData.topHighlights ? '1' : '0',
              followerRecaps: formData.followerRecaps ? '1' : '0',
            }
          : undefined,
    };
    updateMarketingOptIn(data);
    if (data.notificationTags) {
      OneSignal.User.addTags(data.notificationTags);
    }
  };

  return (
    <Section>
      <Header>
        <HeaderTitle>Notification Preferences</HeaderTitle>
      </Header>
      <Content>
        <div className="flex flex-col gap-4">
          <div className="flex flex-auto flex-col gap-2 [&_input:disabled+div]:border-grey-4">
            <h5 className={`preflight preflight-h5 pb-3 ${FieldLabel.defaults.className}`}>Sign me up for:</h5>
            <CheckBox
              label="zoolife Community Newsletter & Offers"
              checked={formData.isMarketingOptIn}
              onChange={(event: JSX.TargetedEvent<HTMLInputElement, Event>) =>
                setFormData((p) => ({ ...p, isMarketingOptIn: event.currentTarget.checked }))
              }
            />
            {errorMessage && <FieldError>{errorMessage}</FieldError>}
            <CheckBox
              name="postReactions"
              label="Replies, Comments & Reactions on My Journal"
              checked={formData.postReactions}
              disabled={!isPushGranted}
              onChange={(event: JSX.TargetedEvent<HTMLInputElement, Event>) =>
                setFormData((p) => ({ ...p, postReactions: event.currentTarget.checked }))
              }
            />
            <CheckBox
              name="topHighlights"
              label="New & Noteworthy Moments"
              checked={formData.topHighlights}
              disabled={!isPushGranted}
              onChange={(event: JSX.TargetedEvent<HTMLInputElement, Event>) =>
                setFormData((p) => ({ ...p, topHighlights: event.currentTarget.checked }))
              }
            />
            <CheckBox
              name="followerRecaps"
              label="Weekly Highlights (for the animals I follow)"
              checked={formData.followerRecaps}
              disabled={!isPushGranted}
              onChange={(event: JSX.TargetedEvent<HTMLInputElement, Event>) =>
                setFormData((p) => ({ ...p, followerRecaps: event.currentTarget.checked }))
              }
            />
            <CheckBox
              name="animalActivityAlerts"
              label="Animal Activity Alerts (for the animals I follow)"
              checked={formData.animalActivityAlerts}
              disabled={!isPushGranted}
              onChange={(event: JSX.TargetedEvent<HTMLInputElement, Event>) =>
                setFormData((p) => ({ ...p, animalActivityAlerts: event.currentTarget.checked }))
              }
            />
            {!isPushGranted &&
              (!isPushSupported ? (
                <p className="preflight preflight-p">
                  Push notifications aren’t supported on this browser. Please use a different device or browser to manage
                  your preferences.
                </p>
              ) : pushPermissionNative === 'default' ? (
                <p className="preflight preflight-p">
                  <button
                    type="button"
                    className="preflight preflight-button font-bold text-primary-green hover:text-secondary-green"
                    onClick={() => OneSignal.Notifications.requestPermission()}
                  >
                    Enable notifications
                  </button>{' '}
                  in your browser to manage preferences.
                </p>
              ) : (
                <p className="preflight preflight-p">Enable push notifications in your browser settings.</p>
              ))}
          </div>
          <ButtonContainer className="mt-4">
            <PrimaryButton onClick={updateHandler}>
              {isPending ? 'Updating...' : isSuccess ? 'Updated ✔' : 'Update'}
            </PrimaryButton>
          </ButtonContainer>
        </div>
      </Content>
    </Section>
  );
};

export default MarketingOptInSection;
